.root {
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(246, 243, 238, 0.9);
  border-bottom: 1px solid var(--border);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  min-height: var(--header-h);
}

.inner {
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: var(--header-h);
  width: min(100%, var(--max-w));
  margin: 0 auto;
  padding: 0 clamp(16px, 3vw, 40px);
}

/* Grows so the period group and actions stay right-aligned — the period
   group needs to be a direct child of .inner (not nested with the actions)
   so it can take a row of its own on very narrow screens. */
.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1 1 auto;
}

.mark {
  width: 22px;
  height: 22px;
  color: var(--amber);
  flex-shrink: 0;
}

.appName {
  font-family: var(--font-display);
  font-size: 1.02rem;
  font-weight: 700;
  color: var(--text);
  white-space: nowrap;
}

.activeUser {
  font-size: 0.92rem;
  color: var(--muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.activeUser::before {
  content: "/";
  margin-right: 4px;
  color: var(--border-md);
}

/* margin-left keeps the actions on the right edge on the Workouts tab, where
   there is no period group to take up the slack. */
.actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  margin-left: auto;
}

.periodGroup {
  display: flex;
  gap: 2px;
  padding: 3px;
  background: var(--surface-2);
  border-radius: 9px;
  flex-shrink: 0;
}

/* min-width + flex-basis 0 keeps all four the same width regardless of label
   length ("7d" vs "90d" vs "1y"), so the group reads as a segmented control
   rather than four differently sized chips. */
.periodBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 0;
  min-width: 52px;
  min-height: 32px;
  padding: 4px 8px;
  border-radius: 7px;
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--soft);
  transition: background 0.12s, color 0.12s;
}

.periodBtn:hover:not(.active) {
  color: var(--text);
  background: var(--tint-md);
}

/* No hover and no pointer: clicking the selected segment does nothing, so the
   cursor should not promise otherwise. */
.periodBtn.active {
  background: var(--amber-strong);
  color: #FFFFFF;
  cursor: default;
}

.compare {
  padding: 7px 14px;
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--steel);
  border: 1px solid rgba(62, 122, 148, 0.35);
  border-radius: var(--radius-sm);
  transition: border-color 0.12s, color 0.12s, background 0.12s;
}

.compare:hover {
  border-color: var(--steel);
  background: var(--steel-dim);
}

.newSearch {
  padding: 7px 14px;
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--soft);
  border: 1px solid var(--border-md);
  border-radius: var(--radius-sm);
  transition: border-color 0.12s, color 0.12s;
}

.newSearch:hover {
  border-color: var(--amber-border);
  color: var(--text);
}

@media (max-width: 600px) {
  .activeUser {
    display: none;
  }

  /* Brand shrinks to its content so the period group can absorb the leftover
     width — on touch these are the primary targets in the header. */
  .brand {
    flex: 0 1 auto;
  }

  .periodGroup {
    flex: 1 1 auto;
    min-width: 0;
  }

  .periodBtn {
    min-width: 0;
    min-height: 34px;
    padding: 4px 6px;
  }

  /* Sized to the same touch band as the period group so the control row is one
     consistent height. */
  .compare,
  .newSearch {
    display: inline-flex;
    align-items: center;
    min-height: 36px;
  }

  .newSearch {
    padding: 6px 10px;
  }
}

/* Default: show full name/label, hide compact abbreviation */
.appNameCompact { display: none; }
.newSearchCompact { display: none; }

@media (max-width: 600px) {
  /* Both labels go compact at the same width the period buttons start
     stretching — the row is shared, so the width freed here is what makes the
     period buttons comfortably tappable. */
  .appNameCompact { display: inline; }
  .appNameFull { display: none; }
  .newSearchCompact { display: inline; }
  .newSearchFull { display: none; }
}

@media (max-width: 430px) {
  /* Period buttons + Compare + New Search share one row with the brand, so
     everything around them is tightened — every pixel saved here goes to the
     period buttons, which stretch to fill whatever is left. */
  .inner {
    gap: 8px;
  }

  .brand {
    gap: 4px;
  }

  .mark {
    width: 16px;
    height: 16px;
  }

  .appName {
    font-size: 0.9rem;
    overflow: hidden;
  }

  .actions {
    gap: 6px;
  }

  .periodGroup {
    gap: 1px;
    padding: 2px;
  }

  .periodBtn {
    padding: 4px 2px;
    font-size: 0.8rem;
  }

  .compare,
  .newSearch {
    padding: 6px 8px;
    font-size: 0.8rem;
  }
}

@media (max-width: 359px) {
  /* Below this the four buttons can't stay comfortably tappable next to the
     brand and both actions, so the group takes a full-width row of its own. */
  .inner {
    flex-wrap: wrap;
    row-gap: 8px;
    padding-top: 8px;
    padding-bottom: 8px;
  }

  .periodGroup {
    order: 3;
    flex: 1 0 100%;
  }
}
